import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/context/LanguageContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompareProvider } from "@/context/CompareContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingActions from "@/components/FloatingActions";
import AppointmentBanner from "@/components/AppointmentBanner";
import Home from "@/pages/Home";
import Stock from "@/pages/Stock";
import Compare from "@/pages/Compare";
import VehicleDetail from "@/pages/VehicleDetail";
import Services from "@/pages/Services";
import Simulator from "@/pages/Simulator";
import Share from "@/pages/Share";
import Sell from "@/pages/Sell";
import Showroom from "@/pages/Showroom";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminVehicles from "@/pages/admin/AdminVehicles";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminSite from "@/pages/admin/AdminSite";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminSimulator from "@/pages/admin/AdminSimulator";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminShowroom from "@/pages/admin/AdminShowroom";
import OnTheRoad from "@/pages/OnTheRoad";
import "@/App.css";

function AdminGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/60">…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;
  return children;
}

function PublicLayout() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isLogin = location.pathname === "/login";
  return (
    <>
      {!isAdminRoute && !isLogin && <Navbar />}
      {!isAdminRoute && !isLogin && location.pathname !== "/" && <div className="pt-20"><AppointmentBanner /></div>}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/stock" element={<Stock />} />
        <Route path="/stock/:id" element={<VehicleDetail />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/services" element={<Services />} />
        <Route path="/sell" element={<Sell />} />
        <Route path="/showroom" element={<Showroom />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/simulator" element={<Simulator />} />
        <Route path="/sur-nos-routes" element={<OnTheRoad />} />
        <Route path="/share" element={<Share />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={<AdminGuard><AdminLayout /></AdminGuard>}>
          <Route index element={<AdminDashboard />} />
          <Route path="vehicles" element={<AdminVehicles />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="contacts" element={<AdminContacts />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="showroom" element={<AdminShowroom />} />
          <Route path="simulator" element={<AdminSimulator />} />
          <Route path="site" element={<AdminSite />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isAdminRoute && !isLogin && <Footer />}
      {!isAdminRoute && !isLogin && <FloatingActions />}
    </>
  );
}

export default function App() {
  return (
    <div className="App grain min-h-screen bg-[#0D0D0D] text-white">
      <HelmetProvider>
        <LanguageProvider>
          <AuthProvider>
            <CompareProvider>
              <BrowserRouter>
                <PublicLayout />
              </BrowserRouter>
              <Toaster theme="dark" position="top-right" richColors />
            </CompareProvider>
          </AuthProvider>
        </LanguageProvider>
      </HelmetProvider>
    </div>
  );
}
